import { getApiConfig } from './config';
import { ApiResponse, ApiError } from './types';
import { logger } from '../logger';

const API_BASE_URL = getApiConfig().baseURL;

/**
 * ApiClient
 *
 * httpOnly 쿠키 전환 이후:
 * - 브라우저에서 직접 Spring Boot를 호출할 때는 /api/proxy/[...path] Next.js 프록시를 경유합니다.
 * - 프록시가 httpOnly 쿠키에서 JWT를 꺼내 Authorization 헤더로 Spring Boot에 주입합니다.
 * - setToken / clearToken은 하위 호환 유지를 위해 남겨두지만 실제 토큰 주입은 프록시가 담당합니다.
 */
export class ApiClient {
  private baseURL: string;
  private proxyBaseURL: string;
  private timeout: number;

  /**
   * @deprecated httpOnly 쿠키 전환 이후 사용되지 않습니다.
   * Authorization 헤더 주입은 /api/proxy 가 담당합니다.
   */
  private token?: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.proxyBaseURL = '/api/proxy';
    this.timeout = getApiConfig().timeout;
  }

  /** @deprecated 프록시 방식 전환으로 불필요. 하위 호환 유지. */
  setToken(token: string) {
    this.token = token;
  }

  /** @deprecated 프록시 방식 전환으로 불필요. 하위 호환 유지. */
  clearToken() {
    this.token = undefined;
  }

  /**
   * 요청 URL 결정:
   * - 브라우저 환경: /api/proxy/... (Next.js 프록시 경유, httpOnly 쿠키 → Authorization 헤더 자동 주입)
   * - 서버 환경(SSR 없음, CSR only 프로젝트이므로 해당 없음): 직접 Spring Boot URL
   */
  private buildURL(endpoint: string): string {
    if (typeof window !== 'undefined') {
      // 앞의 슬래시 제거 후 프록시 경로 조합
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
      return `${this.proxyBaseURL}/${cleanEndpoint}`;
    }
    return `${this.baseURL}${endpoint}`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    customTimeout?: number
  ): Promise<T> {
    const url = this.buildURL(endpoint);

    const config: RequestInit = {
      mode: 'same-origin',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // 디버깅 로그
    if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
      console.group(`API Request: ${options.method || 'GET'} ${url}`);
      console.log('Request Config:', {
        method: options.method || 'GET',
        headers: config.headers,
        body: options.body,
      });
    }

    // 타임아웃 설정
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), customTimeout || this.timeout);

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // 디버깅 로그
      if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
        console.log('Response Status:', response.status, response.statusText);
        console.log('Response Headers:', Object.fromEntries(response.headers.entries()));
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: ApiError = {
          status: response.status,
          message: errorData.message || `API Error: ${response.status} ${response.statusText}`,
          details: errorData,
        };

        // 401 전역 처리 (로그인 페이지 제외)
        if (response.status === 401) {
          try {
            if (typeof window !== 'undefined') {
              const path = window.location?.pathname || '';
              if (!path.startsWith('/login')) {
                this.clearToken();
                // 쿠키 삭제는 서버사이드 route가 담당하므로 호출
                await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
                alert('세션이 만료되었습니다. 다시 로그인해 주세요.');
                window.location.href = '/login';
              }
            }
          } catch {}
        }

        if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging' && response.status >= 500) {
          logger.error('API Error', error);
        }

        throw error;
      }

      const data = await response.json();

      // 응답 바디 기반 401 처리
      try {
        if (
          data &&
          typeof data === 'object' &&
          'status' in data &&
          (data as any).status === 401
        ) {
          if (typeof window !== 'undefined') {
            const path = window.location?.pathname || '';
            if (!path.startsWith('/login')) {
              this.clearToken();
              await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
              const message =
                typeof (data as any).message === 'string' && (data as any).message.length > 0
                  ? (data as any).message
                  : '세션이 만료되었습니다. 다시 로그인해 주세요.';
              alert(message);
              window.location.href = '/login';
            }
          }
        }
      } catch {
        // 전역 401 처리 로직에서 예외가 발생해도 API 흐름은 유지
      }

      // 디버깅 로그
      if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
        console.log('Response Data:', data);
        console.groupEnd();
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
          console.groupEnd();
        }
        throw new Error('요청 시간이 초과되었습니다.');
      }

      if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
        logger.error('Request Failed', error);
        console.groupEnd();
      }

      if (error instanceof Error) {
        throw error;
      }

      logger.error('API Request failed', error);
      throw error;
    }
  }

  // GET 요청
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...(options || {}) });
  }

  // POST 요청
  async post<T>(endpoint: string, data?: any, options?: RequestInit, customTimeout?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...(options || {}),
    }, customTimeout);
  }

  // PUT 요청
  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...(options || {}),
    });
  }

  // PATCH 요청
  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...(options || {}),
    });
  }

  // DELETE 요청
  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...(options || {}) });
  }

  // Blob 다운로드 (엑셀 등)
  //
  // 엑셀/PDF 생성은 본질적으로 장시간 작업이라 일반 JSON 요청용 this.timeout(10~20s)로는
  // 대용량(수만 row) 응답이 도착하기 전에 AbortController가 abort → "AbortError" 발생.
  // 프록시(/api/proxy)가 이미 30분(headersTimeout/bodyTimeout, maxDuration)까지 대기하도록
  // 확장돼 있으므로, 클라이언트 기본 타임아웃도 여기에 맞춘다. 호출부에서 customTimeout으로 재정의 가능.
  private readonly BLOB_DOWNLOAD_TIMEOUT = 1_800_000; // 30분

  async downloadBlob(endpoint: string, data?: any, customTimeout?: number): Promise<Blob> {
    const url = this.buildURL(endpoint);

    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: data ? JSON.stringify(data) : undefined,
    };

    // 디버깅 로그
    if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
      console.group(`Blob Download: POST ${url}`);
      console.log('Request Body:', data);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), customTimeout ?? this.BLOB_DOWNLOAD_TIMEOUT);

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: ApiError = {
          status: response.status,
          message: errorData.message || `Download Error: ${response.status} ${response.statusText}`,
          details: errorData,
        };

        if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
          logger.error('Download Error', error);
          console.groupEnd();
        }

        throw error;
      }

      const blob = await response.blob();

      if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
        console.log('Blob Downloaded:', blob.size, 'bytes');
        console.groupEnd();
      }

      return blob;
    } catch (error) {
      clearTimeout(timeoutId);

      if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_API_ENV === 'staging') {
        logger.error('Download Failed', error);
        console.groupEnd();
      }

      throw error;
    }
  }

  // 파일 업로드
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = this.buildURL(endpoint);

    const config: RequestInit = {
      method: 'POST',
      credentials: 'same-origin',
      // Content-Type은 FormData가 자동으로 설정 (multipart/form-data + boundary)
      body: formData,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: ApiError = {
          status: response.status,
          message: errorData.message || `Upload Error: ${response.status} ${response.statusText}`,
          details: errorData,
        };
        throw error;
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      logger.error('File upload failed', error);
      throw error;
    }
  }
}

// 싱글톤 인스턴스
export const apiClient = new ApiClient();

// API 클라이언트와 서비스들을 한 곳에서 export
export { getApiConfig } from './config';
export type {
  ApiResponse,
  PaginationParams,
  PaginationResponse,
  ApiError
} from './types';

// 서비스들
export { MawbService } from './services';
export type {
  MawbData,
  MawbListParams,
  MawbCreateData,
  MawbUpdateData
} from './services';
